import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    AppNotificationService,
    AppNotificationType,
    assignModelOptions,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigService,
    CONSUME_CODE,
    CSE_STOCK,
    CseStockService,
    EventService,
    MATERIAL_SOURCE_CODE,
    MaterialBillableChangeableModel,
    MaterialBillingIndicatorModel,
    MaterialModel,
    MaterialService,
    OdataMaterialService,
    SYNC_STATUS,
    SynchronizationService,
    UserMasterService,
    UserModel,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, of, Subject } from 'rxjs';
import { catchError, exhaustMap, map, take, takeUntil, tap } from 'rxjs/operators';
import ConfirmationMaterialStockModel from './confirmation-material-stock.model';

@Component({
    selector: 'pace-confirmation-material-stock',
    templateUrl: './confirmation-material-stock.component.html',
    styleUrls: ['./confirmation-material-stock.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationMaterialStockComponent implements OnInit, OnDestroy {
    @Input()
    public model: ConfirmationMaterialStockModel;

    @Input()
    public isBootStock: boolean;

    @Input()
    public syncStatus: SYNC_STATUS;

    public cseHeader: string;
    private userMasterData: UserModel;

    public offlineMessage = '';
    public dateTime = '';
    public isReverse = false;
    public orderField = '';
    private readonly dateTimeFormat: string;
    public isSearchingOtherBootStock = false;
    public isPickingOtherStock = false;

    public confirmationMaterialStockFormChildren: FormArray;
    public confirmationMaterialStockForm: FormGroup;

    private pickUpMaterialStock$ = new Subject<MaterialModel[]>();
    private destroy$ = new Subject<void>();
    public constructor(
        public readonly modalInstance: BsModalRef,
        private readonly configService: ConfigService,
        private readonly userMasterService: UserMasterService,
        private readonly translate: TranslateService,
        private readonly synchronizationService: SynchronizationService,
        private readonly appNotificationService: AppNotificationService,
        private readonly materialService: MaterialService,
        private readonly odataMaterialService: OdataMaterialService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly cseStockService: CseStockService,
        private readonly formBuilder: FormBuilder,
        private readonly changeDetector: ChangeDetectorRef,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private eventService: EventService,
    ) {
        this.userMasterData = this.userMasterService.getSelectedUserModel();
        this.dateTimeFormat = this.clientManager.getDateTimeFormat();
    }

    public ngOnInit(): void {
        this.confirmationMaterialStockFormChildren = this.formBuilder.array([]);
        this.confirmationMaterialStockForm = this.formBuilder.group({
            formChildren: this.confirmationMaterialStockFormChildren,
        });

        this.cseHeader = <string>(
            this.translate.instant(
                this.isBootStock ? 'CONFIRMATION_MATERIAL_BOOT_STOCK' : 'CONFIRMATION_MATERIAL_OTHER_STOCK',
            )
        );

        if (this.isBootStock && this.syncStatus === SYNC_STATUS.SYNC_FAILED) {
            this.offlineMessage = <string>this.translate.instant('CONFIRMATION_MATERIAL_BOOT_STOCK_OFFLINE_LABEL');
            this.dateTime = this.synchronizationService.getLastSyncBootStockTime().format(this.dateTimeFormat);
        }
        this.pickUpMaterialStock$
            .asObservable()
            .pipe(
                take(1),
                exhaustMap((pickedMaterials) => {
                    const materials = pickedMaterials.map((material) => {
                        material.deliveredQuantity = material.deliveredQuantity - (material.quantity.value ?? 0);
                        material.maxQuantity = material.quantity.value ?? 0;
                        material.quantity = assignModelOptions(
                            material.quantity,
                            {
                                ...material.quantity.options,
                                minValue: 1, // For material consumption screen, minimum quantity value should be 1
                                maxValue: material.quantity.value,
                            },
                            material,
                        );
                        material.materialSource.sourceCode = this.isBootStock
                            ? MATERIAL_SOURCE_CODE.BOOTSTOCK
                            : MATERIAL_SOURCE_CODE.OTHERSTOCK;
                        // important as we need to update the consumption code values
                        material.setConsumptionCodes();
                        // if cse stock material, Return option needs to be set based on one consumption code (Consumed)
                        material.setReturnTypeOptions(CONSUME_CODE.CONSUMED);
                        material.activityDate.value = moment();
                        if (material.materialSource.sourceCode === MATERIAL_SOURCE_CODE.BOOTSTOCK) {
                            material.isConsumed = true;
                        }
                        this.model.consumptionList.addModel(material);
                        return material;
                    });
                    return this.materialService.addMaterialsToLocalStorage(materials);
                }),
                tap(() => this.eventService.triggerMaterialPicked()),
                catchError((error) => {
                    this.log.error(
                        'Error in ConfirmationMaterialStockComponent -> pickupFromStock -> materialService.addMaterialToLocalStorage: ',
                        error,
                    );
                    return of(undefined);
                }),
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                this.isPickingOtherStock = false;
                this.modalInstance.hide();
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
    }

    /**
     * sorting the table
     *
     * @param field the field to sort
     */
    public setOrderBy(field: string): void {
        if (this.orderField === field) {
            this.isReverse = !this.isReverse;
        } else {
            this.orderField = field;
        }
    }

    /**
     * checks if any material selected for pickup
     *
     * @returns true if any material selected
     */
    public isMaterialSelected(): boolean {
        return this.model.cseMaterialStocks.some(
            (cseMaterialStock) => cseMaterialStock.quantity.value && cseMaterialStock.quantity.value > 0,
        );
    }

    /**
     * Pickup an item from stock
     */
    public pickupFromStock(): void {
        // check for form validation
        if (this.confirmationMaterialStockForm.invalid) {
            this.appNotificationService.setAppNotification(
                AppNotificationType.WARN,
                <string>this.translate.instant('CONFIRMATION_MATERIAL_CSE_STOCK_ADD_ERROR_NO_TITLE'),
                <string>this.translate.instant('CONFIRMATION_MATERIAL_CSE_STOCK_ERROR_NO_BODY'),
            );
            return;
        }
        if (this.model.cseMaterialStocks) {
            this.isPickingOtherStock = true;
            const pickedMaterials = this.model.cseMaterialStocks.filter(
                (material) => material.quantity.value && material.quantity.value > 0,
            );
            if (this.configService.getEnableBillingIndicator()) {
                this.updateMaterialsBillingIndicators(pickedMaterials)
                    .pipe(take(1))
                    .subscribe((materials) => this.pickUpMaterialStock$.next(materials));
            } else {
                this.pickUpMaterialStock$.next(pickedMaterials);
            }
        }
    }

    private updateMaterialsBillingIndicators(pickedMaterials: MaterialModel[]): Observable<MaterialModel[]> {
        const notification = this.model.notification;
        if (typeof notification === 'undefined') {
            return of([]);
        }
        const materialBillingModel = pickedMaterials.map(
            (material) =>
                new MaterialBillableChangeableModel(
                    material.materialIdentifier.materialNumber.value,
                    '',
                    '',
                    material.quantity.value?.toString(),
                ),
        );
        const billingIndicatorModel = new MaterialBillingIndicatorModel(
            notification.identifier.getQmnum(),
            notification.identifier.getManum(),
            notification.taskGroup,
            materialBillingModel,
        );
        return this.cseStockService.updateMatBillingIndicator(pickedMaterials, billingIndicatorModel);
    }

    public filteredMaterials(materials: MaterialModel[]): MaterialModel[] {
        const materialNumberFilter = this.model.materialNumber.value;
        const descriptionFilter = this.model.materialDesc.value;
        if (
            materials.length === 0 ||
            !this.isBootStock ||
            this.confirmationMaterialStockForm.invalid ||
            (materialNumberFilter.length > 0 && descriptionFilter.length > 0)
        ) {
            return materials;
        }

        const getFilterResult = (searchableTerm: string, searchTerm: string): boolean =>
            searchTerm !== '' && searchableTerm.toLocaleLowerCase().indexOf(searchTerm.toLocaleLowerCase()) === -1;

        return materials
            .filter(
                (material) => !getFilterResult(material.materialIdentifier.materialNumber.value, materialNumberFilter),
            )
            .filter((material) => !getFilterResult(material.description.value, descriptionFilter));
    }

    /**
     * Search the stock based on FrontID, MaterialNumber and StorageLocation
     */
    public searchStock(): void {
        // check for form validation
        this.model.showNoStockAvailable = false;
        if (this.confirmationMaterialStockForm.invalid) {
            this.appNotificationService.setAppNotification(
                AppNotificationType.WARN,
                <string>this.translate.instant('CONFIRMATION_MATERIAL_CSE_STOCK_ADD_ERROR_NO_TITLE'),
                <string>this.translate.instant('CONFIRMATION_MATERIAL_CSE_STOCK_ERROR_NO_BODY'),
            );
            return;
        }
        this.isSearchingOtherBootStock = true;
        this.odataMaterialService
            .getMaterialOtherStock(
                this.userMasterData,
                this.model.materialNumber.value,
                this.model.storageLocation.value,
            )
            .pipe(
                map((materialStockResponse) => {
                    this.model.cseMaterialStocks = this.cseStockService.createCseStockMaterialModels(
                        materialStockResponse,
                        this.model.notification,
                        this.model.consumptionList,
                        CSE_STOCK.OTHER_STOCK,
                    );
                    if (this.model.cseMaterialStocks.length === 0) {
                        this.model.showNoStockAvailable = true;
                    }
                }),
                catchError((error) => {
                    this.log.error('Error', JSON.stringify(error, Object.getOwnPropertyNames(error)));
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('CONFIRMATION_MATERIAL_CSE_OTHER_STOCK_SEARCH_ERROR_TITLE'),
                        <string>this.translate.instant('CONFIRMATION_MATERIAL_CSE_OTHER_STOCK_SEARCH_ERROR_BODY'),
                    );
                    return of(undefined);
                }),
            )
            .subscribe(() => {
                this.isSearchingOtherBootStock = false;
                this.changeDetector.detectChanges();
            });
    }

    public validateForm(): void {
        this.changeDetector.detectChanges();
    }
}
